import { inspectorConversationPath } from "../inspectorConversationPath";

// Batch B3+B9 — pure logic for the Evals tab's run-from-UI + per-item
// drill-down. EvalsPages.jsx is a host view-shell (excluded from coverage);
// all testable logic lives here so it stays 100% Vitest-covered.

// ─── B9 · per-item drill-down ────────────────────────────────────────

// Normalize the /runs/{id}/items response into a plain array of run items.
export function normalizeRunItems(resp) {
  if (resp && resp.success === false) return [];
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp)) return resp;
  return [];
}

// ─── UI-4 · pagination over the paginated backend envelopes ──────────
//
// The runs-list and run-items endpoints return
//   { success, data: [...], pagination: { total, limit, offset } }.
// `unwrapPage` reduces that to `{ items, total, limit, offset }` and tolerates
// bare arrays / older envelopes with no pagination block (falls back to the
// item count so the UI never under-reports). `fallbackLimit` seeds `limit`
// when the server omits it.
export function unwrapPage(resp, fallbackLimit = 25) {
  const items = normalizeRunItems(resp);
  const p = resp && typeof resp === "object" ? resp.pagination : null;
  // A present + finite numeric field wins; null / undefined / junk → fallback.
  const field = (v, dflt) =>
    v != null && Number.isFinite(Number(v)) ? Number(v) : dflt;
  return {
    items,
    total: p ? field(p.total, items.length) : items.length,
    limit: p ? field(p.limit, fallbackLimit) : fallbackLimit,
    offset: p ? field(p.offset, 0) : 0,
  };
}

// "1–25 of 30" (1-indexed, inclusive). "0 of 0" when there is nothing to show.
// Used by both pagers (recent-runs list + per-item drill-down).
export function pageLabel(offset, limit, total) {
  const t = Math.max(0, pgNum(total));
  if (t === 0) return "0 of 0";
  const off = Math.max(0, pgNum(offset));
  const start = Math.min(off + 1, t);
  const end = Math.min(off + Math.max(1, pgNum(limit)), t);
  return `${start}–${end} of ${t}`;
}

// Prev is available whenever we're past the first page.
export function hasPrevPage(offset) {
  return pgNum(offset) > 0;
}
// Next is available whenever the current page doesn't reach the total.
export function hasNextPage(offset, limit, total) {
  return pgNum(offset) + Math.max(1, pgNum(limit)) < pgNum(total);
}
// Keep an offset on a valid page after the total shrinks (e.g. a run that
// reported fewer items than a stale page offset). Snaps down to the last page.
export function clampOffset(offset, limit, total) {
  const lim = Math.max(1, pgNum(limit));
  const t = Math.max(0, pgNum(total));
  const off = Math.max(0, pgNum(offset));
  const maxOff = t === 0 ? 0 : Math.floor((t - 1) / lim) * lim;
  return off > maxOff ? maxOff : off;
}

// Local finite-coerce (evalRunFormat's `num` is declared later in the file).
function pgNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// ─── searchable agent dropdown (kickoff, Screen 1) ───────────────────
//
// Case-insensitive type-to-filter over the agent list: matches the visible
// name first, then falls back to the id (so pasting an id also finds it). A
// blank query returns the full list; a non-array input returns [].
export function filterAgents(agents, query) {
  const list = Array.isArray(agents) ? agents : [];
  const q = String(query == null ? "" : query).trim().toLowerCase();
  if (!q) return list.filter(Boolean);
  return list.filter((a) => {
    if (!a) return false;
    const name = String(a.name || "").toLowerCase();
    const id = String(a.id || "").toLowerCase();
    return name.includes(q) || id.includes(q);
  });
}

// Keyboard highlight movement for the combobox listbox of `count` options.
// `current` may be -1 (nothing highlighted yet); `delta` is +1 (ArrowDown) or
// -1 (ArrowUp). Wraps at the ends; count 0 → -1 (nothing to highlight).
export function nextHighlight(current, delta, count) {
  const n = pgNum(count);
  if (n <= 0) return -1;
  const cur = Number.isInteger(current) ? current : -1;
  if (cur < 0) return delta > 0 ? 0 : n - 1;
  return (cur + delta + n) % n;
}

// ─── UI-3 · expected-vs-actual per row ───────────────────────────────
//
// The EXPECTED answer for a run-item row, joined from the golden dataset item
// (by dataset_item_id). Null when unresolved / blank so the caller can show a
// clear "(no expected answer recorded)" state instead of an empty cell.
export function expectedOutputForItem(runItem, itemsById) {
  const dsItem = itemsById && runItem ? itemsById[runItem.dataset_item_id] : null;
  const exp = dsItem ? dsItem.expected_output : null;
  return exp != null && String(exp) !== "" ? String(exp) : null;
}

// The ACTUAL agent reply captured on a run-item — the new `output` field
// (older rows used prediction/answer). Null when nothing was captured (blank
// or missing) so the caller can render a "no reply captured" state, not blank.
export function actualOutputForItem(runItem) {
  if (!runItem) return null;
  const raw = runItem.output != null ? runItem.output
    : runItem.prediction != null ? runItem.prediction
    : runItem.answer;
  if (raw == null) return null;
  const s = String(raw);
  return s.trim() === "" ? null : s;
}

// Render a single Score for display. Categorical → its string value;
// boolean → "true"/"false"; numeric → trimmed number; else an em-dash.
export function formatScoreValue(score) {
  if (!score) return "—";
  const dt = String(score.data_type || "").toUpperCase();
  if (dt === "CATEGORICAL") return score.string_value || "—";
  if (dt === "BOOLEAN") {
    if (score.value === 1 || score.value === true) return "true";
    if (score.value === 0 || score.value === false) return "false";
    return score.string_value || "—";
  }
  // NUMERIC / unknown → prefer a numeric value, then a string value.
  if (typeof score.value === "number" && Number.isFinite(score.value)) {
    return String(Number(score.value.toFixed(4)));
  }
  if (score.string_value) return score.string_value;
  return "—";
}

// Aggregate counts for the drill-down header: how many items carry ≥1 score.
export function summarizeRunItems(items) {
  const list = Array.isArray(items) ? items : [];
  let scored = 0;
  for (const it of list) {
    if (it && Array.isArray(it.scores) && it.scores.length > 0) scored++;
  }
  return { total: list.length, scored, unscored: list.length - scored };
}

// ─── B3 · run-from-UI ────────────────────────────────────────────────

// A UI-triggered run needs a name; derive a sortable, unique one.
export function defaultRunName(date = new Date()) {
  const iso = date.toISOString().replace(/[:.]/g, "-");
  return `ui-run-${iso}`;
}

// ─── detail-id encoding (route carries dataset id + run_id + run_name) ──
//
// The evals detail route is /analytics/observability/evals/:detailId — a
// single path segment. The run-detail view needs THREE things: the dataset
// id, the local `run_id` (a UUID — poll status by this), and the Langfuse
// `run_name` (fetch per-item drill-down by this). We pack them as
// `${datasetId}~${runId}~${runName}` and split on the FIRST TWO separators;
// dataset ids + run_ids never contain "~", but run_names might, so the
// run_name gets everything after the second separator.

const SEP = "~";

export function buildRunDetailId(datasetId, runId, runName) {
  return `${datasetId || ""}${SEP}${runId || ""}${SEP}${runName || ""}`;
}

export function parseRunDetailId(detailId) {
  const s = String(detailId || "");
  const i = s.indexOf(SEP);
  // Bare id (no separator) — legacy link carrying only a run key/name.
  if (i === -1) return { datasetId: "", runId: "", runName: s };
  const j = s.indexOf(SEP, i + 1);
  // Legacy 2-part `${datasetId}~${runName}` — no run_id was encoded.
  if (j === -1) {
    return { datasetId: s.slice(0, i), runId: "", runName: s.slice(i + 1) };
  }
  return {
    datasetId: s.slice(0, i),
    runId: s.slice(i + 1, j),
    // run_name may itself contain the separator → take the remainder.
    runName: s.slice(j + 1),
  };
}

// ─── E3 · run execution (kickoff → poll → results) ───────────────────
//
// Pure helpers for the "Run eval" UX: poll-gating, aggregate accuracy,
// progress, and per-item verdict reads. All logic lives here so the
// EvalsPages.jsx view-shell (coverage-excluded) stays a thin renderer.

// Runs that are still executing — poll their status while this is true.
// Terminal statuses (completed | failed | cancelled) stop the poll loop.
const IN_FLIGHT_STATUSES = new Set(["queued", "running"]);

export function isRunInFlight(status) {
  return IN_FLIGHT_STATUSES.has(String(status || "").toLowerCase());
}

// Coerce a possibly-missing numeric field to a finite number (default 0).
function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// Accuracy DENOMINATOR excludes ERRORED items (agent/judge failures aren't
// counted for or against accuracy) → "passed / (total − errored)".
export function accuracyBasis(run) {
  const r = run || {};
  const passed = num(r.passed);
  const denom = Math.max(0, num(r.total_items) - num(r.errored));
  return `${passed} / ${denom}`;
}

// Aggregate accuracy as a display percentage, consistent with the basis
// above (errored excluded). "—" when there's no scorable denominator yet.
export function accuracyPct(run) {
  const r = run || {};
  const passed = num(r.passed);
  const denom = num(r.total_items) - num(r.errored);
  if (denom <= 0) return "—";
  const pct = (passed / denom) * 100;
  // One decimal, trailing ".0" trimmed (91.5% / 100% / 63.6%).
  return `${Math.round(pct * 10) / 10}%`;
}

// Progress-bar fill (0–100 integer) = completed_items / total_items.
export function progressPct(run) {
  const r = run || {};
  const total = num(r.total_items);
  if (total <= 0) return 0;
  const pct = (num(r.completed_items) / total) * 100;
  return Math.max(0, Math.min(100, Math.round(pct)));
}

// Per-item verdict — read the categorical `eval_verdict` score and
// normalize to one of ACCURATE / INACCURATE / PARTIAL / ERROR. Returns
// null when the item hasn't been scored yet (still pending/scoring).
export function verdictOfItem(item) {
  const scores = item && Array.isArray(item.scores) ? item.scores : [];
  const sc = scores.find(s => s && s.name === "eval_verdict");
  if (!sc) return null;
  const v = String(sc.string_value || "").toUpperCase();
  return v || null;
}

// Solid swatch colors for the outcome chips/counters — the mockup's
// green / pink / orange / gray, theme-independent (verbatim swatch hues).
const CHIP_COLORS = {
  passed: "#10B981",
  failed: "#EC4899",
  partial: "#F59E0B",
  errored: "#9090A8",
};

// Breakdown chips for a run: passed / failed / partial / errored counts.
export function runOutcomeChips(run) {
  const r = run || {};
  return [
    { key: "passed", label: "Passed", count: num(r.passed), color: CHIP_COLORS.passed },
    { key: "failed", label: "Failed", count: num(r.failed), color: CHIP_COLORS.failed },
    { key: "partial", label: "Partial", count: num(r.partial), color: CHIP_COLORS.partial },
    { key: "errored", label: "Errored", count: num(r.errored), color: CHIP_COLORS.errored },
  ];
}

// Which scorer decided this item's verdict — derived from the eval_verdict
// score's comment (score_prediction records the method there: "numeric
// tolerance match" / "exact match" / "substring match…" / "keyword coverage…"
// or the judge's free-text reason). Honest: never claims "judge" for a
// deterministically-scored item.
export function scoringMethodOf(item) {
  const scores = item && Array.isArray(item.scores) ? item.scores : [];
  const sc = scores.find(s => s && s.name === "eval_verdict");
  const c = String((sc && sc.comment) || "").toLowerCase();
  if (c.startsWith("numeric")) return "numeric";
  if (c.startsWith("exact")) return "exact";
  if (c.startsWith("substring")) return "substring";
  if (c.startsWith("keyword")) return "keyword";
  return verdictOfItem(item) ? "judge" : "—";
}

// Index dataset items by their Langfuse id — for joining a run item
// (dataset_item_id) back to its question text.
export function indexDatasetItems(items) {
  const map = {};
  for (const it of Array.isArray(items) ? items : []) {
    if (it && it.id) map[it.id] = it;
  }
  return map;
}

// Merge the run's linked items with the dataset's full item list so EVERY
// question renders — one row per dataset item, overlaid with its run-item
// (matched by dataset_item_id) when present. Without this, an item whose turn
// produced no Langfuse trace or that errored (and so was never linked as a
// run-item) silently vanished from the table, making a 37-item run "only show
// a few questions". A dataset item with no run-item becomes a verdict-less
// row (scores: []). Falls back to the raw run items when the dataset list
// isn't loaded yet, so the table is never blanked. Orphan run-items (whose
// dataset_item_id isn't in the dataset list) are appended defensively.
export function mergeRunItemsWithDataset(runItems, datasetItems) {
  const runs = Array.isArray(runItems) ? runItems : [];
  const ds = Array.isArray(datasetItems) ? datasetItems : [];
  if (ds.length === 0) return runs;
  const dsIds = new Set(ds.map((d) => d && d.id).filter((x) => x != null));
  const runByItemId = {};
  for (const r of runs) {
    if (r && r.dataset_item_id != null) runByItemId[r.dataset_item_id] = r;
  }
  const rows = ds
    .filter((it) => it && it.id != null)
    .map((it) => runByItemId[it.id] || { id: it.id, dataset_item_id: it.id, scores: [] });
  for (const r of runs) {
    const key = r && r.dataset_item_id;
    // Append only real orphans (a run-item with a key not in the dataset).
    // Skip null / keyless run rows — they can't be joined or rendered.
    if (key != null && !dsIds.has(key)) rows.push(r);
  }
  return rows;
}

// The question text for a run item, joined from the dataset items by id.
// Returns null when the input isn't available (so the UI shows "—" rather
// than a meaningless cuid).
export function questionForItem(runItem, itemsById) {
  const dsItem = itemsById && runItem ? itemsById[runItem.dataset_item_id] : null;
  const input = dsItem && dsItem.input;
  return input ? String(input) : null;
}

// Per-item execution latency in ms — read from the numeric `latency_ms` score
// the engine writes on each item's trace. Null when it wasn't recorded (e.g.
// the item errored before producing a trace). See `formatLatency` to display.
export function latencyOf(item) {
  const scores = item && Array.isArray(item.scores) ? item.scores : [];
  const sc = scores.find(s => s && s.name === "latency_ms");
  const v = sc ? Number(sc.value) : NaN;
  return Number.isFinite(v) ? v : null;
}

// ms → "4.2s" / "820ms"; em-dash when unavailable.
export function formatLatency(ms) {
  if (!Number.isFinite(ms)) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
}

// Deep-link target for one run-item row.
//
// The eval run-item carries a Langfuse `trace_id`, but there is no
// trace-keyed viewer: the tenant-facing surface is the Conversation
// Inspector, keyed by conversation id (Posture B keeps the Langfuse UI
// platform-team-only, so a raw trace link is not an option). The backend
// resolves trace_id → conversation_id via `messages.langfuse_trace_id` and
// returns it on the item; this turns that into the SPA path.
//
// Null when the item has no conversation — an item that never ran (merged in
// from the golden set), or a turn whose span was dropped fail-open. Callers
// MUST render those non-navigable rather than routing somewhere that cannot
// resolve: `/analytics/observability/traces/:id` has no route and silently
// falls through to the Datasets list, which is the bug this replaced.
export function inspectorPathForItem(item) {
  return inspectorConversationPath(item && item.conversation_id);
}
